import {
  UpsellBannerState,
  UpsellCategory,
  UpsellContext,
  UpsellOffer,
  UpsellTierOffer,
  UpsellUsage,
} from '@/entities/pricing';

import { AppLocale, Translate } from '@/shared/i18n';
import { getIntlLocale } from '@/shared/i18n/formatLocale';
import { ResultDownloadKind } from '@/shared/lib/resultDownload';
import { PaymentInterval, PlanCategory } from '@/shared/types/Billing';

export type UpsellIcon =
  | 'bookOpen'
  | 'calendarPlus'
  | 'checkCircle'
  | 'clockCheck'
  | 'discount'
  | 'faceContent'
  | 'fileDownload'
  | 'globe'
  | 'subtitles'
  | 'time'
  | 'translate'
  | 'voice';

export type UpsellRow = { id: string; icon: UpsellIcon; text: string };

const PLAN_NAME_KEYS = {
  [PlanCategory.Business]: 'pricing.plan.business',
  [PlanCategory.Creator]: 'pricing.plan.creator',
  [PlanCategory.CreatorPro]: 'pricing.plan.creatorPro',
} as const satisfies Record<UpsellCategory, string>;

export const getUpsellPlanName = (category: UpsellCategory, t: Translate) => t(PLAN_NAME_KEYS[category]);

export const formatUpsellNumber = (locale: AppLocale, value: number) =>
  new Intl.NumberFormat(getIntlLocale(locale), { maximumFractionDigits: 1 }).format(value);

export const formatUpsellPrice = (locale: AppLocale, amount: number, currency: string) => {
  const fractionDigits = amount % 100 === 0 ? 0 : 2;

  return new Intl.NumberFormat(getIntlLocale(locale), {
    currency: currency.toUpperCase(),
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
    style: 'currency',
  }).format(amount / 100);
};

const isYearly = (interval: PaymentInterval) => interval === PaymentInterval.Year;

export const getUpsellCtaLabel = (offer: UpsellOffer, t: Translate, locale: AppLocale) => {
  const amount = formatUpsellPrice(locale, offer.amount, offer.currency);
  const price = t(isYearly(offer.interval) ? 'upsell.price.perYear' : 'upsell.price.perMonth', { price: amount });

  return offer.kind === 'annual'
    ? t('upsell.action.switchToAnnual', { price })
    : t('upsell.action.upgradeTo', { plan: getUpsellPlanName(offer.category, t), price });
};

export const getUpsellSecondaryLabel = (offer: UpsellOffer, t: Translate) =>
  offer.kind === 'annual'
    ? t('upsell.action.orSwitchToAnnual')
    : t('upsell.action.orUpgradeTo', { plan: getUpsellPlanName(offer.category, t) });

const getMultiplier = (offer: UpsellTierOffer) => offer.targetMinutes / offer.currentMinutes;

const getMonthlyMinutesKey = (offer: UpsellTierOffer) =>
  offer.category === PlanCategory.CreatorPro ? 'upsell.feature.minutesPerMonthShort' : 'upsell.feature.minutesPerMonth';

const minutesPerPeriod = (offer: UpsellTierOffer, t: Translate, locale: AppLocale) =>
  t(isYearly(offer.interval) ? 'upsell.feature.minutesPerYear' : getMonthlyMinutesKey(offer), {
    minutes: formatUpsellNumber(locale, offer.targetMinutes),
  });

const minutesInstead = (offer: UpsellTierOffer, t: Translate, locale: AppLocale) =>
  t(isYearly(offer.interval) ? 'upsell.feature.minutesInsteadYear' : 'upsell.feature.minutesInsteadMonth', {
    from: formatUpsellNumber(locale, offer.currentMinutes),
    minutes: formatUpsellNumber(locale, offer.targetMinutes),
  });

const savings = (offer: UpsellOffer, locale: AppLocale) =>
  offer.kind === 'annual' ? formatUpsellPrice(locale, offer.savings, offer.currency) : '';

type Copy = { title: string; rows: UpsellRow[] };

export const getWelcomeCopy = (context: UpsellContext, offer: null | UpsellTierOffer, t: Translate, locale: AppLocale) => {
  const minutes = isYearly(context.interval)
    ? t('upsell.welcome.minutesYear', { minutes: formatUpsellNumber(locale, context.monthlyMinutes * 12) })
    : t('upsell.welcome.minutesMonth', { minutes: formatUpsellNumber(locale, context.monthlyMinutes) });
  const planRows: Record<UpsellCategory, UpsellRow[]> = {
    [PlanCategory.Business]: [
      { id: 'dictionary', icon: 'bookOpen', text: t('upsell.welcome.dictionary') },
      { id: 'multilanguage', icon: 'translate', text: t('upsell.welcome.multilanguage') },
    ],
    [PlanCategory.Creator]: [
      { id: 'voice', icon: 'voice', text: t('upsell.welcome.voiceCloning') },
      { id: 'languages', icon: 'globe', text: t('upsell.welcome.languages') },
    ],
    [PlanCategory.CreatorPro]: [
      { id: 'lipSync', icon: 'subtitles', text: t('upsell.welcome.lipSyncSubtitles') },
      { id: 'srt', icon: 'fileDownload', text: t('upsell.welcome.srtVoices') },
    ],
  };

  const extras =
    offer?.category === PlanCategory.Business
      ? [t('upsell.welcome.dictionary'), t('upsell.welcome.multilanguage')]
      : [t('upsell.welcome.lipSyncDownloads'), t('upsell.welcome.subtitlesSrt')];

  return {
    title: t('upsell.welcome.title', { plan: getUpsellPlanName(context.category, t) }),
    offer: offer && {
      extras,
      text: t(isYearly(offer.interval) ? 'upsell.welcome.offerYear' : 'upsell.welcome.offerMonth', {
        minutes: formatUpsellNumber(locale, offer.targetMinutes),
        plan: getUpsellPlanName(offer.category, t),
      }),
    },
    rows: [{ id: 'minutes', icon: 'time', text: minutes }, ...planRows[context.category]] satisfies UpsellRow[],
  };
};

export const getDownloadDoneLabel = (kind: ResultDownloadKind | undefined, t: Translate) => {
  if (kind === 'audio') return t('upsell.download.doneAudio');
  if (kind === 'subtitles') return t('upsell.download.doneSubtitles');

  return t('upsell.download.done');
};

export const getDownloadCopy = (
  offer: UpsellOffer,
  t: Translate,
  locale: AppLocale,
  highlight?: 'lipsync' | 'subtitles',
): Copy => {
  if (offer.kind === 'annual') {
    return {
      title: t('upsell.download.annualTitle'),
      rows: [
        {
          id: 'pool',
          icon: 'calendarPlus',
          text: t('upsell.feature.poolDayOne', { minutes: formatUpsellNumber(locale, offer.pool) }),
        },
        { id: 'stay', icon: 'clockCheck', text: t('upsell.feature.minutesStay') },
        { id: 'save', icon: 'discount', text: t('upsell.feature.saveYear', { amount: savings(offer, locale) }) },
      ],
    };
  }

  if (offer.category === PlanCategory.Business) {
    return {
      title: t('upsell.download.businessTitle'),
      rows: [
        { id: 'multilanguage', icon: 'translate', text: t('upsell.download.multilanguage') },
        { id: 'dictionary', icon: 'bookOpen', text: t('upsell.download.dictionary') },
        { id: 'minutes', icon: 'time', text: minutesInstead(offer, t, locale) },
      ],
    };
  }

  const lipSync: UpsellRow = { id: 'lipSync', icon: 'faceContent', text: t('upsell.download.lipSync') };
  const subtitles: UpsellRow = { id: 'subtitles', icon: 'subtitles', text: t('upsell.download.subtitles') };
  const features = highlight === 'subtitles' ? [subtitles, lipSync] : [lipSync, subtitles];

  return {
    title: t('upsell.download.creatorProTitle'),
    rows: [...features, { id: 'minutes', icon: 'time', text: minutesPerPeriod(offer, t, locale) }],
  };
};

export const getUsageLine = (context: UpsellContext, usage: UpsellUsage, t: Translate, locale: AppLocale) =>
  t(isYearly(context.interval) ? 'upsell.usage.usedYear' : 'upsell.usage.usedMonth', {
    total: formatUpsellNumber(locale, usage.total),
    used: formatUpsellNumber(locale, Math.round(usage.used)),
  });

const getTierTitle = (context: UpsellContext, offer: UpsellTierOffer, t: Translate, locale: AppLocale) => {
  const plan = getUpsellPlanName(offer.category, t);

  if (context.grid === 'D') {
    return t('upsell.usage.tierMultiplierTitle', { multiplier: formatUpsellNumber(locale, getMultiplier(offer)), plan });
  }

  return t(isYearly(offer.interval) ? 'upsell.usage.tierYearTitle' : 'upsell.usage.tierMonthTitle', {
    minutes: formatUpsellNumber(locale, offer.targetMinutes),
    plan,
  });
};

export const getUsageCopy = (context: UpsellContext, offer: UpsellOffer, t: Translate, locale: AppLocale): Copy => {
  if (offer.kind === 'annual') {
    return {
      title: t('upsell.usage.annualTitle'),
      rows: [
        {
          id: 'pool',
          icon: 'calendarPlus',
          text: t('upsell.feature.poolUpfront', { minutes: formatUpsellNumber(locale, offer.pool) }),
        },
        { id: 'stay', icon: 'clockCheck', text: t('upsell.feature.minutesStay') },
        { id: 'save', icon: 'discount', text: t('upsell.feature.save', { amount: savings(offer, locale) }) },
      ],
    };
  }

  const featureRows: UpsellRow[] =
    offer.category === PlanCategory.Business
      ? [
          { id: 'multilanguage', icon: 'translate', text: t('upsell.feature.multilanguage') },
          { id: 'dictionary', icon: 'bookOpen', text: t('upsell.feature.dictionary') },
        ]
      : [
          { id: 'lipSync', icon: 'faceContent', text: t('upsell.feature.lipSync') },
          { id: 'subtitles', icon: 'subtitles', text: t('upsell.feature.subtitles') },
        ];

  return {
    title: getTierTitle(context, offer, t, locale),
    rows: [{ id: 'minutes', icon: 'time', text: minutesPerPeriod(offer, t, locale) }, ...featureRows],
  };
};

export const getTierMultiplierLabel = (context: UpsellContext, offer: UpsellTierOffer, t: Translate, locale: AppLocale) =>
  context.grid === 'D' ? t('upsell.hero.multiplier', { multiplier: formatUpsellNumber(locale, getMultiplier(offer)) }) : null;

type BannerCopyOptions = {
  isCompact?: boolean;
};

export const getBannerCopy = (
  context: UpsellContext,
  { offer, reason }: Pick<UpsellBannerState, 'offer' | 'reason'>,
  t: Translate,
  locale: AppLocale,
  { isCompact = false }: BannerCopyOptions = {},
) => {
  const isUsage = reason === 'usage';
  const usedHalf = t(isYearly(context.interval) ? 'upsell.banner.usedHalfYear' : 'upsell.banner.usedHalfMonth');

  if (offer.kind === 'annual') {
    const values = { amount: savings(offer, locale), minutes: formatUpsellNumber(locale, offer.pool) };
    const fullText = t(isUsage ? 'upsell.banner.annualNowText' : 'upsell.banner.annualDayOneText', values);

    return {
      lead: isUsage ? usedHalf : t('upsell.banner.fullYearTitle'),
      text: isCompact ? t('upsell.banner.annualShortText', values) : fullText,
    };
  }

  const isBusiness = offer.category === PlanCategory.Business;
  const minutes = formatUpsellNumber(locale, offer.targetMinutes);

  if (isUsage) {
    const keys = isBusiness
      ? (['upsell.banner.businessGivesMonth', 'upsell.banner.businessGivesYear'] as const)
      : (['upsell.banner.creatorProGivesMonth', 'upsell.banner.creatorProGivesYear'] as const);

    return { lead: usedHalf, text: t(keys[isYearly(offer.interval) ? 1 : 0], { minutes }) };
  }

  return {
    lead: t(isBusiness ? 'upsell.banner.businessTitle' : 'upsell.banner.creatorProTitle'),
    text: t(isBusiness ? 'upsell.banner.businessText' : 'upsell.banner.creatorProText', { minutes }),
  };
};
